import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Checkbox, Typography } from "@material-tailwind/react";
import { useNavigate } from "react-router-dom";
import { api_key, auth_token, sdkURL } from "../utils/api-url-list";
import { constants } from "../utils/ranges";
import { ReactComponent as MaleIcon } from "../assets/icons/Male.svg";
import { ReactComponent as FemaleIcon } from "../assets/icons/Female.svg";
import { ReactComponent as CheckFilled } from "../assets/icons/CheckFilled.svg";
import { ReactComponent as HumanShort } from "../assets/icons/HumanShort.svg";
import { ReactComponent as HumanTall } from "../assets/icons/HumanTall.svg";
import { ReactComponent as HumanSlim } from "../assets/icons/HumanSlim.svg";
import { ReactComponent as HumanFat } from "../assets/icons/HumanFat.svg";

// const languages = {
//   en: { nativeName: "English", englishName: "English" },
//   // ar: { nativeName: "العربية", englishName: "Arabic" },
//   // ur: { nativeName: "اردو", englishName: "Urdu" },
//   // es: { nativeName: "español", englishName: "Spanish" },
//   // hi: { nativeName: "हिन्दी", englishName: "Hindi" },
//   // ml: { nativeName: "മലയാളം", englishName: "Malayalam" },
// };
// const CLIENT_LOCATIONS = {
//   CPEMIL: {
//     Maharashtra: ["Mumbai", "Pune"],
//     Delhi: ["Delhi"],
//     Karnataka: ["Bangalore"],
//     Telangana: ["Hyderabad"],
//     Gujarat: ["Ahmedabad"],
//     "Tamil Nadu": ["Chennai"],
//     "West Bengal": ["Kolkata"],
//     Rajasthan: ["Jaipur"],
//     "Uttar Pradesh": ["Lucknow"],
//     Chandigarh: ["Chandigarh"],
//     "Madhya Pradesh": ["Bhopal"],
//     Odisha: ["Bhubaneshwar"],
//   },
// };
const cmToFeetAndInches = (cm) => {
  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const remainingInches = Math.round(totalInches % 12);
  return `${feet} ft ${remainingInches} in`;
};
const kgToLb = (kg) => `${Math.round(kg * 2.20462)} lb`;
const calculateBMI = (heightCm, weightKg) => {
  const heightM = heightCm / 100;
  const bmi = weightKg / heightM ** 2;
  return bmi.toFixed(2);
};

const SelectOptions = () => {
  const { t, i18n } = useTranslation("SelectOptions");
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [hasConsent, setConsent] = useState(false);
  const [employeeCodeError, setEmployeeCodeError] = useState("");
  const [hcpCode, setHcpCode] = useState("");
  const [employeeCode, setEmployeeCode] = useState("");
  const [hcpCodeError, setHcpCodeError] = useState("");
  const [loading, setLoading] = useState(false);

  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const dob = useMemo(() => {
    try {
      const trim_age = age.trim();
      const parsedAge = parseInt(trim_age, 10);
      if (isNaN(parsedAge) || parsedAge < 1 || parsedAge > 110) {
        return "";
      }
      const currentDate = new Date();
      const year = currentDate.getFullYear() - parsedAge;
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const date = String(currentDate.getDate()).padStart(2, "0");
      return `${year}-${month}-${date}`;
    } catch {
      return "";
    }
  }, [age]);
  const [height, setHeight] = useState(174);
  const [weight, setWeight] = useState(62);
  const [heightMetric, setHeightMetric] = useState("cm");
  const [weightMetric, setWeightMetric] = useState("kg");
  const heightBubbleTextPos = useMemo(
    () => ((height - 50) * 100) / (250 - 50),
    [height],
  );
  const weightBubbleTextPos = useMemo(
    () => ((weight - 20) * 100) / (220 - 20),
    [weight],
  );
  const bmi = useMemo(() => {
    const calculatedBMI = calculateBMI(height, weight);
    const selectedBMIRangeIndex = constants.BMI.ranges.reduce(
      (p, c) => (calculatedBMI > c.point ? c : p),
      0,
    );
    return { value: calculatedBMI, range: selectedBMIRangeIndex.label };
  }, [height, weight]);

  const validateCodes = async () => {
    try {
      setLoading(true);
      const resp = await fetch(sdkURL("auth/check-referral-code"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth_token,
        },
        body: JSON.stringify({
          api_key: api_key,
          referral_codes: [
            {
              code: hcpCode,
              type: "HCP",
            },
            {
              code: employeeCode,
              type: "EMP",
            },
          ],
        }),
      });
      const respJSON = await resp.json();
      if (respJSON?.statusCode?.toString().startsWith("2")) return respJSON;
      else
        throw new Error(
          respJSON?.data?.message ?? "Error in validating the codes",
        );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const next = async () => {
    switch (page) {
      case 1:
        if (!hcpCode || !employeeCode) {
          setHcpCodeError("HCP Code is required");
          setEmployeeCodeError("Employee Code is required");
          return;
        }
        try {
          const response = await validateCodes();

          if (response?.all_codes_valid) {
            setHcpCodeError("");
            setEmployeeCodeError("");

            if (hasConsent) {
              setPage(2);
            }
          } else {
            const hcp = response?.data?.find((item) => item.type === "HCP");
            const emp = response?.data?.find((item) => item.type === "EMP");

            if (hcp && !hcp.valid) {
              setHcpCodeError(hcp.reason || "Invalid HCP Code");
            } else {
              setHcpCodeError("");
            }

            if (emp && !emp.valid) {
              setEmployeeCodeError(emp.reason || "Invalid Employee Code");
            } else {
              setEmployeeCodeError("");
            }
          }
        } catch (error) {
          setHcpCodeError("Validation failed. Try again.");
          setEmployeeCodeError("Validation failed. Try again.");
        }
        break;
      case 2:
        if ((gender === "Male" || gender === "Female") && dob.length > 0)
          setPage(3);
        break;
      case 3:
        navigate("/start-scan", {
          replace: true,
          state: {
            hasConsent,
            hcpCode,
            employeeCode,
            gender,
            dob,
            height,
            weight,
          },
        });
        break;
      default:
        break;
    }
  };

  const back = () => {
    if (page === 1) navigate(-1);
    else if (page === 2) setPage(1);
    else setPage((p) => p - 1);
  };

  const cancel = () => {
    setPage(1);
    // setOpenMenu(false);
    setConsent(false);
    setGender("");
    setAge("");
    setHeight(174);
    setWeight(62);
    setHeightMetric("cm");
    setWeightMetric("kg");
  };

  return (
    <section className="shrink-0 grow px-[15%] relative z-10">
      {page === 1 ? (
        <>
          {/* <Typography
            variant="h4"
            className="mb-4 sm:mb-8 text-sm sm:text-2xl text-primary/80"
          >
            {t("SelectLanguage")}
          </Typography>
          <Menu open={openMenu} handler={setOpenMenu}>
            <MenuHandler>
              <Button
                variant="text"
                className="w-full flex items-center justify-between space-x-3 rounded-xl border border-stroke bg-white text-primary"
              >
                <span className="grow text-left rtl:text-right text-sm sm:text-2xl font-normal normal-case">
                  {languages[i18n.resolvedLanguage].nativeName}
                </span>
                <svg
                  className={`h-3 w-3 sm:h-6 sm:w-6 transition-transform ${
                    openMenu ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 41 22"
                  fill="none"
                >
                  <path
                    d="M2 2L20.5 20L39 2"
                    stroke="currentColor"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            </MenuHandler>
            <MenuList className="w-[70%]">
              {Object.keys(languages).map((lng) => (
                <MenuItem
                  key={lng}
                  className={`${
                    i18n.resolvedLanguage === lng
                      ? "bg-primary text-white focus:bg-primary/80 focus:text-white"
                      : "bg-white text-primary focus:bg-transparent focus:text-primary"
                  } text-sm sm:text-2xl font-normal active:bg-primary/80 active:text-white`}
                  onClick={() => changeLanguageTo(lng)}
                >
                  {languages[lng].nativeName}
                </MenuItem>
              ))}
            </MenuList>
          </Menu> */}
          <Typography
            variant="h4"
            className="mb-4 sm:mb-8 text-sm sm:text-2xl text-primary/80"
          >
            {t("EnterHCPCode")}
          </Typography>
          <input
            type="text"
            className={`block relative w-full px-4 py-2 sm:px-6 sm:py-4 rounded-lg outline-none overflow-hidden border ${
              hcpCodeError ? "border-[#f00]" : "border-stroke"
            } bg-white text-sm sm:text-2xl text-primary`}
            id="hcp-code"
            name="hcp-code"
            required
            placeholder={t("HCPCode")}
            autoComplete="off"
            maxLength={100}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setHcpCode(e.target.value);
                setHcpCodeError("");
              }
            }}
            value={hcpCode}
          />
          {hcpCodeError && (
            <p className="mt-2 sm:mt-3 text-[#f00] text-[10px] sm:text-sm leading-none">
              {hcpCodeError}
            </p>
          )}
          <Typography
            variant="h4"
            className="mb-4 mt-8 sm:mb-8 text-sm sm:text-2xl text-primary/80"
          >
            {t("EnterEmployeeCode")}
          </Typography>
          <input
            type="text"
            className={`block relative w-full px-4 py-2 sm:px-6 sm:py-4 rounded-lg outline-none overflow-hidden border ${
              employeeCodeError ? "border-[#f00]" : "border-stroke"
            } bg-white text-sm sm:text-2xl text-primary`}
            id="employee-code"
            name="employee-code"
            required
            placeholder={t("EmployeeCode")}
            autoComplete="off"
            maxLength={100}
            onChange={(e) => {
              if (e.target.value.length <= 100) {
                setEmployeeCode(e.target.value);
                setEmployeeCodeError("");
              }
            }}
            value={employeeCode}
          />
          {employeeCodeError && (
            <p className="mt-2 sm:mt-3 text-[#f00] text-[10px] sm:text-sm leading-none">
              {employeeCodeError}
            </p>
          )}
          {/* <Typography
            variant="h4"
            className="mt-8 sm:mt-16 mb-4 sm:mb-8 text-sm sm:text-2xl text-primary/80"
          >
            {t("EnterUniqueID")}
          </Typography>
          <input
            type="text"
            className="block relative w-full px-4 py-2 sm:px-6 sm:py-4 rounded-lg outline-none overflow-hidden border border-stroke bg-white text-sm sm:text-2xl text-primary"
            id="unique-id"
            name="unique-id"
            required
            placeholder={t("UniqueID")}
            autoComplete="off"
            maxLength={100}
            onChange={(e) =>
              e.target.value.length <= 100 && setUniqueID(e.target.value)
            }
            value={uniqueID}
          /> */}
          <div className="mt-24 sm:mt-48">
            <Checkbox
              id="consent"
              color="indigo"
              label={t("consent")}
              labelProps={{
                className: "text-primary/80 text-[8px] sm:text-base",
              }}
              containerProps={{ className: "shrink-0" }}
              className="border border-stroke bg-white"
              ripple={true}
              value={hasConsent}
              checked={hasConsent}
              onChange={(e) => setConsent(e.target.checked)}
            />
          </div>
        </>
      ) : page === 2 ? (
        <>
          <Typography
            variant="h4"
            className="mb-4 sm:mb-8 lg:mb-6 text-sm sm:text-2xl text-primary/80"
          >
            {t("SelectGender")}
          </Typography>
          <div className="mb-12 sm:mb-24 lg:mb-12 flex items-stretch space-x-6 sm:space-x-12">
            {[
              { label: "Male", Icon: MaleIcon },
              { label: "Female", Icon: FemaleIcon },
            ].map(({ label, Icon }) => (
              <label
                key={`gender-${label}`}
                className="flex flex-col items-center justify-between"
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="gender"
                  checked={gender === label}
                  value={label}
                  onChange={(e) => setGender(e.target.value)}
                />
                <div
                  className={`shrink-0 relative h-16 w-16 sm:h-32 sm:w-32 lg:h-24 lg:w-24 p-4 sm:p-8 lg:p-6 rounded-lg sm:rounded-2xl border border-stroke ${
                    gender === label ? "bg-primary/5" : ""
                  }`}
                >
                  <Icon className="h-full w-full text-primary/80" />
                  {gender === label && (
                    <CheckFilled className="absolute top-2 right-2 sm:top-4 sm:right-4 h-3 w-3 sm:h-6 sm:w-6 lg:h-4 lg:w-4 text-primary" />
                  )}
                </div>
                <span className="mt-2 sm:mt-4 text-sm sm:text-xl text-primary">
                  {t(label)}
                </span>
              </label>
            ))}
          </div>
          <Typography
            variant="h4"
            className="mb-4 sm:mb-8 lg:mb-6 text-sm sm:text-2xl text-primary/80"
          >
            {t("SelectDOB")}
          </Typography>
          <div className="mb-12 sm:mb-24 lg:mb-12 flex rounded-lg overflow-hidden border border-stroke bg-white text-sm sm:text-2xl">
            <input
              className="grow px-4 py-2 sm:px-6 sm:py-4 outline-none"
              type="text"
              name="age"
              id="age"
              placeholder="Enter Your Age"
              autoComplete="off"
              inputMode="numeric"
              required
              value={age}
              onChange={(e) => setAge(e.target.value)}
            />
            <p className="w-32 px-4 py-2 sm:px-6 sm:py-4 bg-primary text-white text-center">
              Years
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="mb-6 sm:mb-8">
            <div className="flex items-start justify-between">
              <Typography
                variant="h4"
                className="text-sm sm:text-2xl text-primary/80"
              >
                {t("YourHeight")}
              </Typography>
              <div dir="ltr" className="shrink-0 grid grid-cols-2 gap-2.5">
                {["cm", "ft"].map((metric) => (
                  <label
                    key={metric}
                    className="px-3 py-1.5 w-16 sm:w-24 flex items-center justify-between rounded-md border border-stroke bg-white text-primary"
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="height-metric"
                      checked={heightMetric === metric}
                      value={metric}
                      onChange={(e) => setHeightMetric(e.target.value)}
                    />
                    <span className="text-xs sm:text-base">{metric}</span>
                    {heightMetric === metric && (
                      <svg
                        className="shrink-0 h-3 w-3 sm:h-4 sm:w-4 ml-2.5"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        fill="none"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between text-primary">
              <HumanShort className="shrink-0 h-5 sm:h-10 lg:h-6" />
              <HumanTall className="shrink-0 h-10 sm:h-20 lg:h-12" />
            </div>
            <div className="relative text-primary">
              <input
                type="range"
                className="w-full outline-none"
                min={50}
                max={250}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
              />
              <output
                dir="ltr"
                className="absolute -top-6 sm:-top-10 -translate-x-1/2 px-2 py-1 rounded-lg bg-primary text-white text-xs sm:text-lg lg:text-base font-semibold tracking-wider whitespace-nowrap"
                style={{
                  left: `calc(${
                    i18n.dir() === "ltr"
                      ? heightBubbleTextPos + "% + "
                      : 100 - heightBubbleTextPos + "% - "
                  }(${8 - heightBubbleTextPos * 0.15}px))`,
                }}
              >
                {heightMetric === "cm"
                  ? `${height} cm`
                  : cmToFeetAndInches(height)}
              </output>
            </div>
            <div className="flex items-start justify-between text-xs sm:text-base text-primary">
              <p dir="ltr">
                {heightMetric === "cm" ? "50 cm" : cmToFeetAndInches(50)}
              </p>
              <p dir="ltr">
                {heightMetric === "cm" ? "250 cm" : cmToFeetAndInches(250)}
              </p>
            </div>
          </div>
          <div className="mb-6 sm:mb-8">
            <div className="flex items-start justify-between">
              <Typography
                variant="h4"
                className="text-sm sm:text-2xl text-primary/80"
              >
                {t("YourWeight")}
              </Typography>
              <div dir="ltr" className="shrink-0 grid grid-cols-2 gap-2.5">
                {["kg", "lb"].map((metric) => (
                  <label
                    key={metric}
                    className="px-3 py-1.5 w-16 sm:w-24 flex items-center justify-between rounded-md border border-stroke bg-white text-primary"
                  >
                    <input
                      className="sr-only"
                      type="radio"
                      name="weight-metric"
                      checked={weightMetric === metric}
                      value={metric}
                      onChange={(e) => setWeightMetric(e.target.value)}
                    />
                    <span className="text-xs sm:text-base">{metric}</span>
                    {weightMetric === metric && (
                      <svg
                        className="shrink-0 h-3 w-3 sm:h-4 sm:w-4 ml-2.5"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                        stroke="currentColor"
                        fill="none"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-4 flex items-end justify-between text-primary">
              <HumanSlim className="shrink-0 h-10 sm:h-20 lg:h-12" />
              <HumanFat className="shrink-0 h-10 sm:h-20 lg:h-12" />
            </div>
            <div className="relative text-primary">
              <input
                type="range"
                className="w-full outline-none"
                min={20}
                max={220}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
              />
              <output
                dir="ltr"
                className="absolute -top-6 sm:-top-10 -translate-x-1/2 px-2 py-1 rounded-lg bg-primary text-white text-xs sm:text-lg lg:text-base font-semibold tracking-wider whitespace-nowrap"
                style={{
                  left: `calc(${
                    i18n.dir() === "ltr"
                      ? weightBubbleTextPos + "% + "
                      : 100 - weightBubbleTextPos + "% - "
                  }(${8 - weightBubbleTextPos * 0.15}px))`,
                }}
              >
                {weightMetric === "kg" ? `${weight} kg` : kgToLb(weight)}
              </output>
            </div>
            <div className="flex items-start justify-between text-xs sm:text-base text-primary">
              <p dir="ltr">{weightMetric === "kg" ? "20 kg" : kgToLb(20)}</p>
              <p dir="ltr">{weightMetric === "kg" ? "220 kg" : kgToLb(220)}</p>
            </div>
          </div>
          <div className="px-2 sm:py-4 py-3 sm:px-6 flex items-start justify-between space-x-2 sm:space-x-4 rounded-2xl border border-stroke bg-stroke/[0.15]">
            <Typography
              variant="h4"
              className="text-primary text-sm sm:text-xl lg:text-base font-medium"
            >
              {t("YourBMI")}
            </Typography>
            <div className="shrink-0 flex flex-col items-end space-y-2">
              <Typography
                variant="h4"
                className="text-primary text-sm sm:text-xl lg:text-base font-medium"
              >
                BMI = {bmi.value} kg/m²
              </Typography>
              <Typography
                variant="h5"
                className="text-black text-xs sm:text-lg lg:text-sm font-medium"
              >
                {t(bmi.range)}
              </Typography>
            </div>
          </div>
        </>
      )}

      <div className="mt-6 sm:mt-8 mb-3 sm:mb-4 flex items-stretch justify-between space-x-8">
        <Button
          color="white"
          className="flex-1 bg-white text-primary/80 border border-primary text-sm sm:text-lg font-medium normal-case"
          onClick={back}
        >
          {t("Back")}
        </Button>
        <Button
          color="white"
          className="flex-1 bg-primary text-white border border-primary text-sm sm:text-lg font-medium normal-case"
          onClick={next}
          disabled={
            (page === 1 &&
              (!hasConsent || !hcpCode || !employeeCode || loading)) ||
            (page === 2 && (!gender || !dob))
          }
        >
          {t("Next")}
        </Button>
      </div>
      {page !== 1 && (
        <Button
          color="white"
          className="mt-2 sm:mt-4 mb-4 w-full bg-white text-primary/80 border border-primary text-sm sm:text-lg font-medium normal-case"
          onClick={cancel}
        >
          {t("Cancel")}
        </Button>
      )}
    </section>
  );
};

export default SelectOptions;
